import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Space, Button, Segmented, Switch, Dropdown, Modal, Input, Tooltip, message } from 'antd';
import { PlusOutlined, EditOutlined, MoreOutlined, ExportOutlined, SettingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import adminProductService from '../../services/adminProductService';
import AdminSubscriptionEditModal from '../../components/billing/AdminSubscriptionEditModal';
import AdminProvisionModal from '../../components/billing/AdminProvisionModal';
import AdminProductManageDrawer from '../../components/billing/AdminProductManageDrawer';
import { TYPE_LABEL, STATUS_COLOR, STATUS_LABEL, CYCLE_LABEL, resourceLink } from '../../components/billing/adminProductMeta';
import { adminPath } from '../../../shared/config/paths';

const fmt = (v) => (v ? String(v).slice(0, 16) : '-');

/** 用户详情「产品」：用户名下全部订阅 + 未挂订阅的资源；行内改套餐/到期/续费、暂停恢复、结束；开通产品。 */
const UserProducts = ({ userId }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ items: [], types: {}, provisionable: [] });
    const [type, setType] = useState('all');
    const [ended, setEnded] = useState(false);
    const [editId, setEditId] = useState(null);
    const [manageId, setManageId] = useState(null);
    const [provisionOpen, setProvisionOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await adminProductService.list(userId, ended);
        setLoading(false);
        if (r?.success) setData(r.data); else message.error(r?.error || '加载失败');
    }, [userId, ended]);

    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => (type === 'all' ? data.items : data.items.filter((i) => i.type === type)), [data, type]);

    const run = async (p, ok) => {
        const r = await p;
        if (r?.success) { message.success(ok); load(); } else message.error(r?.error || r?.message || '操作失败');
    };

    const endNow = (rec) => {
        let reason = '';
        Modal.confirm({
            title: `立即结束订阅 #${rec.subscription_id}？`,
            content: <Input placeholder="原因" onChange={(e) => { reason = e.target.value; }} />,
            okText: '立即结束',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: () => run(adminProductService.end(rec.subscription_id, 'now', reason), '已结束'),
        });
    };

    const actionItems = (rec) => [
        rec.status === 'active' && { key: 'suspend', label: '暂停', onClick: () => run(adminProductService.suspend(rec.subscription_id), '已暂停') },
        rec.status === 'suspended' && { key: 'resume', label: '恢复', onClick: () => run(adminProductService.resume(rec.subscription_id), '已恢复') },
        rec.auto_renew && { key: 'eot', label: '到期不续', onClick: () => run(adminProductService.end(rec.subscription_id, 'end_of_term'), '已关闭自动续费') },
        !['cancelled', 'terminated'].includes(rec.status) && { key: 'end', label: '立即结束', danger: true, onClick: () => endNow(rec) },
    ].filter(Boolean);

    const columns = [
        {
            title: '产品', key: 'product', render: (_, r) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}><Tag>{TYPE_LABEL[r.type] || r.type}</Tag>{r.product_name || <span style={{ color: '#999' }}>无订阅</span>}</Space>
                    {r.subscription_id && <span style={{ fontSize: 12 }}><Link to={adminPath(`/billing/subscriptions?id=${r.subscription_id}`)}>#{r.subscription_id}</Link>{r.addons ? <span style={{ color: '#999' }}>{` · 附加包 ${r.addons}`}</span> : ''}</span>}
                </Space>
            ),
        },
        {
            title: '资源', key: 'res', render: (_, r) => (
                <Space direction="vertical" size={0}>
                    {(r.resources || []).slice(0, 3).map((x) => {
                        const to = resourceLink(x.type, x.id, x.name);
                        return to ? <Link key={`${x.type}-${x.id}`} to={adminPath(to)}>{x.name}</Link> : <span key={`${x.type}-${x.id}`}>{x.name}</span>;
                    })}
                    {(r.resources || []).length > 3 && <span style={{ color: '#999' }}>+{r.resources.length - 3}</span>}
                </Space>
            ),
        },
        {
            title: '状态', dataIndex: 'status', width: 90, render: (s, r) => (
                <Tooltip title={r.failure_reason || undefined}><Tag color={STATUS_COLOR[s] || 'default'}>{STATUS_LABEL[s] || s}</Tag></Tooltip>
            ),
        },
        {
            title: '周期 / 价格', key: 'cycle', width: 120, render: (_, r) => (r.billing_cycle
                ? <span>{CYCLE_LABEL[r.billing_cycle] || r.billing_cycle}{r.price != null ? ` · ${r.price}${r.currency ? ` ${r.currency}` : ''}` : ''}</span> : '-'),
        },
        { title: '到期', dataIndex: 'expiry_date', width: 140, render: fmt },
        { title: '自动续费', dataIndex: 'auto_renew', width: 80, render: (v) => (v == null ? '-' : v ? <Tag color="green">开</Tag> : <Tag>关</Tag>) },
        {
            title: '', key: 'op', width: 150, render: (_, r) => {
                const first = (r.resources || [])[0];
                const to = first && resourceLink(first.type, first.id, first.name);
                return (
                    <Space size={2}>
                        {r.subscription_id && !['cancelled', 'terminated'].includes(r.status) && (
                            <Tooltip title="套餐 / 到期 / 续费"><Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditId(r.subscription_id)} /></Tooltip>
                        )}
                        {r.subscription_id && (
                            <Tooltip title="用量 / 配额 / 附加包"><Button size="small" type="text" icon={<SettingOutlined />} onClick={() => setManageId(r.subscription_id)} /></Tooltip>
                        )}
                        {to && <Tooltip title="资源页"><Link to={adminPath(to)}><Button size="small" type="text" icon={<ExportOutlined />} /></Link></Tooltip>}
                        {r.subscription_id && actionItems(r).length > 0 && (
                            <Dropdown menu={{ items: actionItems(r) }} trigger={['click']}><Button size="small" type="text" icon={<MoreOutlined />} /></Dropdown>
                        )}
                    </Space>
                );
            },
        },
    ];

    const typeOptions = [{ label: `全部 ${data.items.length}`, value: 'all' },
        ...Object.entries(data.types || {}).map(([k, n]) => ({ label: `${TYPE_LABEL[k] || k} ${n}`, value: k }))];

    return (
        <>
            <Space style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }} wrap>
                <Segmented size="small" options={typeOptions} value={type} onChange={setType} />
                <Space>
                    <span style={{ color: '#999', fontSize: 12 }}>含已结束</span>
                    <Switch size="small" checked={ended} onChange={setEnded} />
                    <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setProvisionOpen(true)}>开通产品</Button>
                </Space>
            </Space>
            <Table rowKey="key" size="small" loading={loading} columns={columns} dataSource={rows} pagination={{ pageSize: 20, hideOnSinglePage: true }}
                scroll={{ x: 760 }} locale={{ emptyText: '暂无产品' }} />
            <AdminSubscriptionEditModal open={!!editId} subscriptionId={editId} onClose={() => setEditId(null)} onSaved={load} />
            <AdminProductManageDrawer open={!!manageId} subscriptionId={manageId} onClose={() => setManageId(null)} onChanged={load} />
            <AdminProvisionModal open={provisionOpen} userId={userId} types={data.provisionable || []} onClose={() => setProvisionOpen(false)} onDone={load} />
        </>
    );
};

export default UserProducts;
